import React, { useState } from 'react';
import { Alert, Spinner, RowActions, DataTable, type DataTableColumn } from '@/components/ui';
import { format } from 'date-fns';
import { ProjectDocument } from '../../../types/ProjectDocument';
import { useClientSort } from '../../../utils/useClientSort';
import { formatDate as formatPacificDate } from '@/utils/displayDateTime';
import { confirmAction } from '@/components/ui';

const SORT_VALUES: Record<string, (doc: ProjectDocument) => string | number | null | undefined> = {
  createdAt: (doc) => (doc.createdAt ? new Date(doc.createdAt).getTime() : null),
  attachmentName: (doc) => doc.attachmentName,
  attachmentNotes: (doc) => doc.attachmentNotes,
};

interface OtherDocumentListProps {
  documents: ProjectDocument[];
  loading?: boolean;
  onDownload: (doc: ProjectDocument) => void;
  onDelete: (docId: number) => Promise<void>;
  refreshingId?: number | null;
}

const OtherDocumentList: React.FC<OtherDocumentListProps> = ({
  documents,
  loading = false,
  onDownload,
  onDelete,
  refreshingId = null,
}) => {
  const { sorted, sortBy, sortDir, onSort } = useClientSort(documents, SORT_VALUES);
  // A failed upload has no file to download; say so next to the list instead of a browser alert.
  const [uploadNotice, setUploadNotice] = useState<string | null>(null);
  const formatDate = (date: string | Date) => {
    try {
      return formatPacificDate(date) || 'Invalid date';
    } catch {
      return 'Invalid date';
    }
  };

  if (loading) {
    return (
      <div className="tw-text-center tw-p-4">
        <Spinner>Loading...</Spinner>
      </div>
    );
  }

  if (!documents || documents.length === 0) {
    return (
      <Alert variant="info" className="tw-text-center">
        No other documents found. Click “Add Document” to upload the first one.
      </Alert>
    );
  }

  const handleDownloadClick = (doc: ProjectDocument) => {
    if (doc.attachmentURL?.startsWith?.('placeholder://')) {
      setUploadNotice(`"${doc.attachmentName}" didn't upload, so it can't be downloaded. Delete it and upload the file again.`);
      return;
    }
    onDownload(doc);
  };

  // This delete fired with no confirmation at all (ui-audit §0) — documents are unrecoverable.
  const handleDeleteClick = async (doc: ProjectDocument) => {
    if (!(await confirmAction({ title: `Delete "${doc.attachmentName}"?`, body: 'This cannot be undone.', confirmLabel: 'Delete', destructive: true }))) return;
    onDelete(doc.id);
  };

  // File name is the identifying label, so it's the card title; Date is the secondary line.
  const columns: DataTableColumn<ProjectDocument>[] = [
    {
      key: 'attachmentName',
      header: 'File',
      sortable: true,
      mobile: 'title',
      cell: (doc) => (
        <span className="tw-truncate tw-inline-block" title={doc.attachmentName} style={{ maxWidth: 320 }}>
          {doc.attachmentName}
        </span>
      ),
    },
    {
      key: 'createdAt',
      header: 'Date',
      sortable: true,
      mobile: 'subtitle',
      cell: (doc) => formatDate(doc.createdAt),
    },
    {
      key: 'attachmentNotes',
      header: 'Notes',
      sortable: true,
      cell: (doc) =>
        doc.attachmentNotes ? (
          <span className="tw-truncate tw-inline-block" title={doc.attachmentNotes} style={{ maxWidth: 250 }}>
            {doc.attachmentNotes}
          </span>
        ) : (
          <span className="tw-text-content-muted">-</span>
        ),
    },
    {
      key: 'actions',
      header: 'Actions',
      align: 'right',
      mobile: 'trailing',
      cell: (doc) => (
        <RowActions
          actions={[
            { kind: 'download', label: 'Download', onClick: () => handleDownloadClick(doc) },
            {
              kind: 'delete',
              label: 'Delete Document',
              onClick: () => handleDeleteClick(doc),
              disabled: refreshingId === doc.id,
            },
          ]}
        />
      ),
    },
  ];

  return (
    <div className="tw-w-full">
      {uploadNotice && (
        <Alert variant="warning" dismissible onClose={() => setUploadNotice(null)} className="tw-mb-3">
          {uploadNotice}
        </Alert>
      )}
      <DataTable columns={columns} data={sorted} rowKey={(doc) => doc.id} striped hover sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
    </div>
  );
};

export default OtherDocumentList;

